import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dropdown, Table } from 'semantic-ui-react'
import { MilkMoneyQuery } from '~/QueryHooks/MilkMoneyQuery'
import Dialog from '~/components/Dialog'
import { El } from '~/components/Layout/style'
import { KeyLabelType } from '~/types'
import { formatNumber } from '~/utils/helpers'
import {
  ChevronIcon,
  ExpandableRow,
  ExpandedCell,
  ExpandedContent,
  ExpandedInner,
  HeaderDialog
}
  from './style'
import TableFooter from '~/components/TableComponent/TableFooter'

interface ChangeLogsModalProps {
  months?: KeyLabelType[]
}

const COLUMN_COUNT = 9

const ChangeLogsModal: React.FC<ChangeLogsModalProps> = ({ months }) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())
  const [dataFiltered, setDataFiltered] = useState({
    rows: 10,
    page: 0,
    month: ''
  })

  const {
    data: logsData,
    isLoading,
    refetch: fetchData,
    isFetching: isFetchingLogs
  } = MilkMoneyQuery.getMilkMoneyLogs({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  })

  useEffect(() => {
    fetchData()
  }, [dataFiltered])

  const changesLabelValues = {
    supplierNo: t('Supplier no.'),
    amount: t('Delivered amount (in l)'),
    avgTemp: t('avg. Temp. (°C)'),
    uniqueId: t('Unique id'),
    timeFrom: t('Time from'),
    timeTo: t('Time to')
  }

  function getValueChangeLogCell(value, key) {
    switch (key) {
      case 'date':
        return dayjs(value).format('DD.MM.YYYY HH:mm')
      case 'avgTemp':
        return formatNumber(value, 1)
      case 'amount':
        return formatNumber(value, 1)
      default:
        return value
    }
  }

  function toggleRow(index: number) {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(index)) {
      newExpanded.delete(index)
    } else {
      newExpanded.add(index)
    }
    setExpandedRows(newExpanded)
  }

  function renderChangesTable(changes) {
    const changeItems: { label: string; oldValue: any; newValue: any }[] = []

    if (changes?.[0]) {
      Object.keys(changes[0]).forEach(changeKey => {
        if (Object.keys(changesLabelValues).find(key => key === changeKey)) {
          const label = changesLabelValues[changeKey]
          if (changeKey !== 'uniqueId') {
            changeItems.push({
              label,
              oldValue: getValueChangeLogCell(changes[0][changeKey]?.old, changeKey),
              newValue: getValueChangeLogCell(changes[0][changeKey]?.new, changeKey)
            })
          }
        }
      })
    }

    return (
      <ExpandedInner>
        <Table celled compact size='small'>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{t('Name')}</Table.HeaderCell>
              <Table.HeaderCell>{t('Original value')}</Table.HeaderCell>
              <Table.HeaderCell>{t('New value')}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {changeItems.map((item, idx) => (
              <Table.Row key={idx}>
                <Table.Cell>{item.label}</Table.Cell>
                <Table.Cell>{item.oldValue ?? '-'}</Table.Cell>
                <Table.Cell>{item.newValue ?? '-'}</Table.Cell>
              </Table.Row>
            ))}
            {changeItems.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan={3} textAlign='center'>{t('No changes')}</Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </ExpandedInner>
    )
  }

  function renderTable() {
    const rows = logsData?.rows || []

    return (
      <>
        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell width={1} />
              <Table.HeaderCell>{t('Tour start')}</Table.HeaderCell>
              <Table.HeaderCell>{t('Tour end')}</Table.HeaderCell>
              <Table.HeaderCell>{t('Tour no.')}</Table.HeaderCell>
              <Table.HeaderCell>{t('Supplier no.')}</Table.HeaderCell>
              <Table.HeaderCell>{t('Farmer')}</Table.HeaderCell>
              <Table.HeaderCell>{t('Change type')}</Table.HeaderCell>
              <Table.HeaderCell>{t('User')}</Table.HeaderCell>
              <Table.HeaderCell>{t('Modification date')}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rows.map((row, index) => (
              <React.Fragment key={index}>
                <ExpandableRow onClick={() => toggleRow(index)}>
                  <Table.Cell>
                    <ChevronIcon name='chevron right' $isOpen={expandedRows.has(index)} />
                  </Table.Cell>
                  <Table.Cell>{row.dateStart ? dayjs(row.dateStart).format('DD.MM.YYYY HH:mm') : '-'}</Table.Cell>
                  <Table.Cell>{row.dateEnd ? dayjs(row.dateEnd).format('DD.MM.YYYY HH:mm') : '-'}</Table.Cell>
                  <Table.Cell>{row.tourNumber ?? '-'}</Table.Cell>
                  <Table.Cell>{row.supplierNo ?? '-'}</Table.Cell>
                  <Table.Cell>{row.companyName ?? '-'}</Table.Cell>
                  <Table.Cell>{row.typeLabel ?? '-'}</Table.Cell>
                  <Table.Cell>{row.name ?? '-'}</Table.Cell>
                  <Table.Cell>
                    {row.createdAt ? dayjs(row.createdAt).format('DD.MM.YYYY HH:mm') : '-'}
                  </Table.Cell>
                </ExpandableRow>
                <Table.Row>
                  <ExpandedCell colSpan={COLUMN_COUNT} $isOpen={expandedRows.has(index)}>
                    <ExpandedContent $isOpen={expandedRows.has(index)}>
                      {renderChangesTable(row.changes)}
                    </ExpandedContent>
                  </ExpandedCell>
                </Table.Row>
              </React.Fragment>
            ))}
            {rows.length === 0 && !isLoading && !isFetchingLogs && (
              <Table.Row>
                <Table.Cell colSpan={COLUMN_COUNT} textAlign='center'>{t('No results found....')}</Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
        <TableFooter
          removePagination={false}
          dataFiltered={dataFiltered}
          onChangeData={(data) => { setDataFiltered(data); setExpandedRows(new Set()) }}
          total={logsData?.total || 0}
        />
      </>
    )
  }

  function renderHeader() {
    return (
      <div style={{ display: 'flex', gap: 20, alignItems: 'baseline' }}>
        <HeaderDialog>{t('Change logs')}</HeaderDialog>
        <Dropdown
          selectOnBlur={false}
          placeholder={t('Delivery month tour')}
          selection
          value={dataFiltered.month}
          onChange={(e, data) => setDataFiltered({ ...dataFiltered, month: data.value as string })}
          options={months?.map(option => ({ text: option.label, value: option.key })) || []}
        />
      </div>
    )
  }

  return <>
    <El.PageHeaderActionButton
      size='large'
      onClick={() => {
        if (!dataFiltered.month && months?.length > 0) {
          setDataFiltered({ ...dataFiltered, month: months[0].key })
        }
        setIsOpen(true)
      }}
    >
      {t('Change logs')}
    </El.PageHeaderActionButton>
    <Dialog
      isDialogOpen={isOpen}
      minHeight='400px'
      handleClose={() => {
        setIsOpen(false)
        setExpandedRows(new Set())
      }}
      textHeader={renderHeader()}
      textClose={t('Close')}
      size='fullscreen'
      isLoading={isLoading || isFetchingLogs}
      textContent={renderTable()}
    />
  </>
}

export default ChangeLogsModal
